import {announce} from '@github-ui/aria-live'
import {Banner, Link, LinkButton, useMergedRefs} from '@primer/react'
import {useEffect, useState, type Ref} from 'react'

import type {EntitlementQuotaNotice} from '../hooks/use-entitlement-quota-notice'

export function EntitlementQuotaBanner({
  notice,
  onPrimaryAction,
  onSecondaryAction,
  focusAfterDismiss,
  containerRef,
  className,
}: {
  notice: EntitlementQuotaNotice | null | undefined
  onPrimaryAction?: () => void
  onSecondaryAction?: () => void
  /** Focus a stable control outside the banner before a dismissible notice unmounts. */
  focusAfterDismiss?: () => void
  containerRef?: Ref<HTMLDivElement>
  className?: string
}) {
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null)
  const ref = useMergedRefs(containerRef, setContainerEl)

  useEffect(() => {
    if (notice?.description && containerEl) {
      announce(notice.description, {assertive: notice.variant === 'critical', element: containerEl})
    }
  }, [notice?.description, notice?.variant, containerEl])

  if (!notice) return null
  return (
    <div ref={ref} className={className} data-testid="quota-banner">
      <Banner
        variant={notice.variant}
        title={notice.title}
        hideTitle
        description={notice.description}
        actionsLayout={notice.secondaryAction ? 'stacked' : 'default'}
        onDismiss={
          notice.isDismissable
            ? () => {
                focusAfterDismiss?.()
                notice.onDismiss?.()
              }
            : undefined
        }
        primaryAction={
          notice.primaryAction ? (
            <LinkButton as="a" href={notice.primaryAction.href} onClick={onPrimaryAction}>
              {notice.primaryAction.label}
            </LinkButton>
          ) : undefined
        }
        secondaryAction={
          notice.secondaryAction ? (
            <Link as="a" href={notice.secondaryAction.href} onClick={onSecondaryAction}>
              {notice.secondaryAction.label}
            </Link>
          ) : undefined
        }
      />
    </div>
  )
}
